/**
 * OmopConceptDetailsDialog — read-only details for a single OMOP concept,
 * resolved by concept id (OR-6214). Opened from an inline-concept chip in the
 * Definition tab's "Concept mappings" section. Details (code, vocabulary,
 * domain, standard) come from KNOWLEDGE_MANAGEMENT.OMOP_METADATA.CONCEPT via the
 * by-id lookup endpoint; the chip only carries {conceptId, name}.
 */

import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useIntl } from 'react-intl';

import { useGetOmopConceptByIdQuery } from '@verantos-dev/data-access-concept-groups';

interface OmopConceptDetailsDialogProps {
  /** OMOP concept id to resolve and display. */
  conceptId: number;
  /** Fallback display name from the chip, shown until/if the lookup resolves. */
  fallbackName?: string;
  onClose: () => void;
}

const DetailRow = ({ label, value }: { label: string; value: string }) => (
  <Stack direction="row" spacing={2}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ flex: 1, wordBreak: 'break-word' }}>
      {value}
    </Typography>
  </Stack>
);

export const OmopConceptDetailsDialog = ({
  conceptId,
  fallbackName,
  onClose,
}: OmopConceptDetailsDialogProps) => {
  const { formatMessage } = useIntl();
  const { data, isLoading, isError } = useGetOmopConceptByIdQuery({ conceptId });
  const concept = data?.data;

  const isStandard = concept?.standardConcept === 'S';

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      data-testid="omop-concept-details-dialog"
    >
      <DialogTitle>
        {concept?.conceptName ?? fallbackName ?? formatMessage({ id: 'epiChat.omopConcept.title' })}
      </DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : isError || !concept ? (
          <Typography variant="body2" color="error" data-testid="omop-concept-details-error">
            {formatMessage({ id: 'epiChat.omopConcept.error' })}
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            <DetailRow
              label={formatMessage({ id: 'epiChat.omopConcept.label.name' })}
              value={concept.conceptName}
            />
            <DetailRow
              label={formatMessage({ id: 'epiChat.omopConcept.label.conceptId' })}
              value={String(concept.conceptId)}
            />
            <DetailRow
              label={formatMessage({ id: 'epiChat.omopConcept.label.code' })}
              value={concept.conceptCode}
            />
            <DetailRow
              label={formatMessage({ id: 'epiChat.omopConcept.label.vocabulary' })}
              value={concept.vocabularyId}
            />
            <DetailRow
              label={formatMessage({ id: 'epiChat.omopConcept.label.domain' })}
              value={concept.domainId}
            />
            <DetailRow
              label={formatMessage({ id: 'epiChat.omopConcept.label.standard' })}
              value={formatMessage({
                id: isStandard
                  ? 'epiChat.omopConcept.standard.yes'
                  : 'epiChat.omopConcept.standard.no',
              })}
            />
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};
